import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import ReactDOM from 'react-dom';
import { AuthContext } from '../App';
import BrandLogo from './BrandLogo';
import TradingViewWidget from './trading/TradingViewWidget';
import PriceProvider from './trading/PriceProvider';
import TradeHistory from './trading/TradeHistory';
import EconomicCalendar from './trading/EconomicCalendar';
import FundamentalAnalysis from './trading/FundamentalAnalysis';
import AssetSelector from './trading/AssetSelector';
import WithdrawModal from './trading/WithdrawModal';
import DepositModal from './trading/DepositModal';
import DocumentsModal from './trading/DocumentsModal';
import ChangePasswordModal from './trading/ChangePasswordModal';
import ClientMessages from './trading/ClientMessages';
import SupportButton from './SupportButton';
import { useTradingEngine, BalanceDisplay, ActiveTradeCard } from './trading/TradingEngine';
import { History, ChevronDown, Calendar, BarChart3, TrendingUp, Clock, DollarSign, LogOut, FileText, CreditCard, Wallet, Lock, Smartphone } from 'lucide-react';

const TradingDemo = () => {
  const authContext = useContext(AuthContext);
  const { user, logout } = authContext || {};
  
  const [selectedAsset, setSelectedAsset] = useState('EURUSD');
  const { tt } = useLanguage();
  const [activeTab, setActiveTab] = useState('trade');
  const [tradesTab, setTradesTab] = useState('open');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userMenuPosition, setUserMenuPosition] = useState({ top: 0, right: 0 });
  const userButtonRef = useRef(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  // Настройки клиента управляются через CRM
  const [tradeAmount, setTradeAmount] = useState(25);
  const [expirationTime, setExpirationTime] = useState(300); // 5 минут по умолчанию

  // Определяем данные пользователя
  const userData = user ? {
    name: user.name || user.email || 'Пользователь',
    id: user.platform_id || user.id,  // CRITICAL: Use platform_id (client_id), not user.id
    email: user.email
  } : {
    name: 'Демо Пользователь',
    id: 'DEMO123456789',
    email: 'demo@example.com'
  };
  // Настройки клиента управляются через CRM

  // Интегрируем торговый движок
  const {
    balance,
    activeTrades,
    tradeHistory,
    assetPrices,
    createTrade,
    closeTrade,
    updateAssetPrice,
    refreshData
  } = useTradingEngine(user);

  // Получаем текущую цену выбранного актива
  const currentPrice = assetPrices[selectedAsset] || 0;

  // Обработка обновления цены из TradingView widget или PriceProvider
  const handlePriceUpdate = (asset, price) => {
    if (price === null) {
      // TradingView widget не может предоставить цены (бесплатная версия)
      console.warn(`⚠️ TradingView widget cannot provide price data for ${asset}. Using PriceProvider fallback.`);
      return;
    }
    
    if (price && typeof price === 'number' && price > 0) {
      console.log(`📈 Price update from TradingView for ${asset}:`, price);
      updateAssetPrice(asset, price);
    }
  };

  // Инициализация цен через PriceProvider
  useEffect(() => {
    // Устанавливаем начальную цену для выбранного актива
    const initialPrice = PriceProvider.getCurrentPrice(selectedAsset);
    updateAssetPrice(selectedAsset, initialPrice);

    // Запускаем обновление цен в реальном времени
    const priceUpdateInterval = PriceProvider.startPriceUpdates((asset, price) => {
      updateAssetPrice(asset, price);
    }, 3000);

    return () => {
      PriceProvider.stopPriceUpdates(priceUpdateInterval);
    };
  }, [selectedAsset, updateAssetPrice]);

  // Периодическое обновление цены для синхронизации между таймфреймами
  useEffect(() => {
    const syncPrice = async () => {
      await updateAssetPrice(selectedAsset); // Без параметра price - будет получена из API
    };
    
    // Синхронизируем цену каждые 30 секунд
    const interval = setInterval(syncPrice, 30000);
    
    // Также синхронизируем при смене актива
    syncPrice();
    
    return () => clearInterval(interval);
  }, [selectedAsset, updateAssetPrice]);
  
  // Функция показа ошибки
  const showErrorNotification = (message) => {
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-[999999] transition-all duration-300 transform -translate-y-full text-center max-w-lg';
    notification.textContent = message;
    document.body.appendChild(notification);
    requestAnimationFrame(() => {
      notification.style.transform = 'translateX(-50%) translateY(0)';
    });
    setTimeout(() => {
      notification.style.transform = 'translateX(-50%) translateY(-200%)';
      setTimeout(() => notification.remove(), 300);
    }, 7000);
  };

  // Функции торговли
  const handleBuyTrade = () => {
    const result = createTrade(selectedAsset, 'call', tradeAmount, expirationTime);
    if (result.success) {
      showSuccessNotification(`Сделка КУПИТЬ открыта на ${selectedAsset} на сумму $${tradeAmount}`);
    } else {
      showErrorNotification(result.message);
    }
  };
  
  const handleSellTrade = () => {
    const result = createTrade(selectedAsset, 'put', tradeAmount, expirationTime);
    if (result.success) {
      showSuccessNotification(`Сделка ПРОДАТЬ открыта на ${selectedAsset} на сумму $${tradeAmount}`);
    } else {
      showErrorNotification(result.message);
    }
  };
  
  // Функция показа уведомления об успешной сделке
  const showSuccessNotification = (message) => {
    // Создаем уведомление
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-[999999] transition-all duration-300 transform translate-x-full';
    notification.innerHTML = `
      <div class="flex items-center space-x-2">
        <div class="w-2 h-2 bg-white rounded-full"></div>
        <span class="font-medium">${message}</span>
      </div>
    `;
    
    // Добавляем в DOM
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    // Удаляем через 4 секунды
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 4000);
  };

  // Демо данные убраны - используем реальные данные из торгового движка

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-950 via-emerald-900 to-green-950 mobile-no-scroll"
         style={{ minHeight: '200vh' }}>
      {/* Header - Desktop First, Mobile Responsive */}
      <header className="bg-green-950/30 backdrop-blur-sm border-b border-green-800 px-2 lg:px-6 py-2 lg:py-4 mobile-no-scroll">
        {/* Mobile Layout */}
        <div className="flex flex-col space-y-2 lg:hidden">
          {/* Row 1: User Info - САМЫЙ ВЕРХ на мобильных */}
          <div className="flex items-center justify-between relative w-full">
            {/* User Menu Dropdown - Left on Mobile, Equal width */}
            <div className="relative flex-1 z-[100]">
              <button
                ref={userButtonRef}
                onClick={() => {
                  if (!showUserMenu && userButtonRef.current) {
                    const rect = userButtonRef.current.getBoundingClientRect();
                    // Центрирование выпадающего меню
                    setUserMenuPosition({
                      top: rect.bottom + window.scrollY + 8,
                      left: (window.innerWidth - 256) / 2 // 256px - ширина меню, центрируем
                    });
                  }
                  setShowUserMenu(!showUserMenu);
                }}
                className="w-full flex items-center space-x-1 bg-white/10 rounded-lg px-2 py-1 hover:bg-white/15 transition-colors"
              >
                <div className="text-left flex-1">
                  <div className="text-white font-medium text-xs truncate">{userData.name}</div>
                  <div className="text-gray-400 text-xs">ID: {userData.id}</div>
                </div>
                <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Balance - Right on Mobile, Equal width */}
            <div className="bg-white/10 rounded-lg px-2 py-1 flex-1 ml-1">
              <div className="text-gray-400 text-xs">{tt('Торговый счет')}</div>
              <div className="text-white font-semibold text-sm">
                ${(() => {
                  const safeBalance = parseFloat(balance);
                  return isNaN(safeBalance) ? '0.00' : safeBalance.toFixed(2);
                })()}
              </div>
            </div>
          </div>

          {/* Row 2: Navigation Tabs - НИЖЕ на мобильных */}
          <div className="flex items-center justify-center w-full">
            <div className="flex space-x-1 w-full justify-center">
                <button
                  onClick={() => setActiveTab('calendar')}
                  className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-1 whitespace-nowrap text-sm min-h-[44px] ${
                    activeTab === 'calendar' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-white/10 text-gray-300 hover:bg-white/15'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="sm:inline">{tt('Календарь')}</span>
                </button>
                <button
                  onClick={() => setActiveTab('fundamental')}
                  className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-1 whitespace-nowrap text-sm min-h-[44px] ${
                    activeTab === 'fundamental' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-white/10 text-gray-300 hover:bg-white/15'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="sm:inline">{tt('Анализ')}</span>
                </button>
                <button
                  onClick={() => {
                    setActiveTab('history');
                    if (refreshData) {
                      refreshData();
                    }
                  }}
                  className={`flex-1 px-3 py-2 rounded-lg transition-colors flex items-center justify-center space-x-1 whitespace-nowrap text-sm min-h-[44px] ${
                    activeTab === 'history' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-white/10 text-gray-300 hover:bg-white/15'
                  }`}
                >
                  <History className="w-4 h-4" />
                  <span className="sm:inline">{tt('История')}</span>
                </button>
              </div>
            </div>
        </div>
        
        {/* Desktop Layout - Восстановленный original layout */}
        <div className="hidden lg:flex lg:items-center lg:justify-between">
          {/* Left: Asset Selector */}
          <div className="flex items-center min-w-[300px]">
            <AssetSelector
              selectedAsset={selectedAsset}
              onAssetChange={setSelectedAsset}
              currentPrice={currentPrice}
              assetPrices={assetPrices}
            />
          </div>

          {/* Center: Navigation Tabs */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setActiveTab('calendar')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 whitespace-nowrap ${
                activeTab === 'calendar' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white/10 text-gray-300 hover:bg-white/15'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span>{tt('Календарь')}</span>
            </button>
            <button
              onClick={() => setActiveTab('fundamental')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 whitespace-nowrap ${
                activeTab === 'fundamental' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white/10 text-gray-300 hover:bg-white/15'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span>{tt('Анализ')}</span>
            </button>
            <button
              onClick={() => {
                setActiveTab('history');
                if (refreshData) {
                  refreshData();
                }
              }}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 whitespace-nowrap ${
                activeTab === 'history' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white/10 text-gray-300 hover:bg-white/15'
              }`}
            >
              <History className="w-4 h-4" />
              <span>{tt('История')}</span>
            </button>
          </div>

          {/* Right: User Info & Balance */}
          <div className="flex items-center space-x-4">
            {/* User Menu Dropdown */}
            <div className="relative z-[100]">
              <button
                ref={userButtonRef}
                onClick={() => {
                  if (!showUserMenu && userButtonRef.current) {
                    const rect = userButtonRef.current.getBoundingClientRect();
                    setUserMenuPosition({
                      top: rect.bottom + window.scrollY + 8,
                      right: window.innerWidth - rect.right
                    });
                  }
                  setShowUserMenu(!showUserMenu);
                }}
                className="flex items-center space-x-3 bg-white/10 rounded-lg px-4 py-2 hover:bg-white/15 transition-colors"
              >
                <div className="text-left">
                  <div className="text-white font-medium">{userData.name}</div>
                  <div className="text-gray-400 text-sm">ID: {userData.id}</div>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Balance */}
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <div className="text-gray-400 text-xs">{tt('Торговый счет')}</div>
              <div className="text-white font-semibold">
                ${(() => {
                  const safeBalance = parseFloat(balance);
                  return isNaN(safeBalance) ? '0.00' : safeBalance.toFixed(2);
                })()}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* User Menu Portal - Outside header */}
      {showUserMenu && ReactDOM.createPortal(
        <>
          <div
            className="fixed inset-0"
            style={{zIndex: 99999997, background: 'transparent'}}
            onClick={() => setShowUserMenu(false)}
          />
          <div 
            className="fixed w-64 bg-gray-800 border border-gray-600 rounded-lg shadow-xl" 
            style={{
              top: userMenuPosition.top + 'px',
              left: userMenuPosition.left ? userMenuPosition.left + 'px' : 'auto',
              right: userMenuPosition.right ? userMenuPosition.right + 'px' : 'auto',
              zIndex: 99999998
            }}
          >
            <div className="p-4">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-lg font-semibold text-white">{userData.name.charAt(0).toUpperCase()}</span>
                </div>
                <div>
                  <div className="text-white font-medium">{userData.name}</div>
                  <div className="text-green-400 text-sm">{user ? 'Торговый счет' : 'Демо режим'}</div>
                </div>
              </div>
              
              <div className="space-y-2">
                {user && (
                  <button 
                    onClick={() => {
                      setActiveTab('messages');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    📧 Мои сообщения
                  </button>
                )}
                <button 
                  onClick={() => {
                    setShowDepositModal(true);
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <CreditCard className="w-4 h-4" />
                  <span>{tt('Пополнение')}</span>
                </button>
                <button 
                  onClick={() => {
                    console.log('💸 TradingDemo: Opening withdraw modal with balance:', balance);
                    setShowWithdrawModal(true);
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Wallet className="w-4 h-4" />
                  <span>{tt('Вывод средств')}</span>
                </button>
                <button 
                  onClick={() => {
                    setShowDocumentsModal(true);
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>{tt('Документы')}</span>
                </button>
                <button 
                  onClick={() => {
                    setShowChangePasswordModal(true);
                    setShowUserMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Lock className="w-4 h-4 text-yellow-400" />
                  <span>{tt('Смена пароля')}</span>
                </button>
                <button 
                  onClick={async () => {
                    setShowUserMenu(false);
                    if (window.installPWA) {
                      await window.installPWA();
                    }
                  }}
                  className="w-full text-left px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center space-x-2"
                  data-testid="install-pwa-btn"
                >
                  <Smartphone className="w-4 h-4 text-amber-400" />
                  <span>{tt('Скачать приложение')}</span>
                </button>
                {user && (
                  <>
                    <hr className="border-gray-600 my-2" />
                    <button 
                      onClick={() => {
                        logout();
                        window.location.href = '/';
                      }}
                      className="w-full text-left px-3 py-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{tt('Выйти')}</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      <div className="flex flex-col min-h-[calc(100vh-80px)]">
        {/* Trading Panel - Always Top */}
        <div className="w-full bg-green-900/30 backdrop-blur-sm border-b border-green-700 p-4 flex flex-col flex-shrink-0">
          {/* Asset Selector - Always Show */}
          <div className="mb-4">
            <AssetSelector
              selectedAsset={selectedAsset}
              onAssetChange={setSelectedAsset}
              currentPrice={currentPrice}
              assetPrices={assetPrices}
            />
          </div>
          
          {/* Trading Panel - Horizontal Layout on Desktop, Vertical on Mobile */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-4">{tt('Торговая панель')}</h3>
            
            {/* Vertical Layout - Same for Desktop and Mobile */}
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">{tt('Сумма сделки')}</label>
                <input 
                  type="text"
                  inputMode="decimal"
                  value={tradeAmount} 
                  onChange={(e) => {
                    const value = e.target.value;
                    // Разрешаем только цифры и точку, пустое поле тоже разрешаем
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setTradeAmount(value);
                    }
                  }}
                  className="w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-2" 
                  placeholder={tt('Введите сумму')}
                />
                <div className="text-xs text-gray-400 mt-1">
                  Максимум: ${(() => {
                    const safeBalance = parseFloat(balance);
                    return isNaN(safeBalance) ? '0.00' : safeBalance.toFixed(2);
                  })()}
                </div>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm mb-2">{tt('Время экспирации')}</label>
                <select 
                  value={expirationTime}
                  onChange={(e) => setExpirationTime(Number(e.target.value))}
                  className="w-full bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                >
                  <option value="300">5 минут</option>
                  <option value="900">15 минут</option>
                  <option value="1800">30 минут</option>
                  <option value="3600">1 час</option>
                  <option value="14400">4 часа</option>
                  <option value="86400">24 часа</option>
                  <option value="2592000">30 дней</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm mb-2">{tt('Потенциальная прибыль')}</label>
                <div className="bg-gray-700 rounded px-3 py-2 text-green-400 font-semibold">
                  +${(tradeAmount * 0.85).toFixed(2)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <button 
                  onClick={handleBuyTrade}
                  disabled={tradeAmount > balance || !currentPrice}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 lg:py-3 rounded font-semibold transition-colors flex items-center justify-center space-x-1 lg:space-x-2"
                >
                  <span>↑</span>
                  <span>{tt('КУПИТЬ')}</span>
                </button>
                <button 
                  onClick={handleSellTrade}
                  disabled={tradeAmount > balance || !currentPrice}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 lg:py-3 rounded font-semibold transition-colors flex items-center justify-center space-x-1 lg:space-x-2"
                >
                  <span>↓</span>
                  <span>{tt('ПРОДАТЬ')}</span>
                </button>
              </div>
              
              <div className="text-xs text-gray-400 text-center">
                Сделка откроется по текущей цене
              </div>
            </div>

            {/* Mobile version removed - now using unified vertical layout above */}
          </div>
        </div>

        {/* Main Content - Chart Full Width */}
        <div className="flex-1 flex flex-col bg-gradient-to-br from-green-900 via-green-800 to-green-900 min-h-0">
          {activeTab === 'history' ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">{tt('История сделок')}</h2>
                <div className="flex items-center space-x-4">
                  <div className="bg-gray-800 px-4 py-2 rounded-lg">
                    <span className="text-gray-300 text-sm">{tt('Общий P&L:')}</span>
                    <span className={`font-bold ${
                      tradeHistory.reduce((sum, trade) => sum + (trade.profit || 0), 0) >= 0 
                        ? 'text-green-400' 
                        : 'text-red-400'
                    }`}>
                      ${tradeHistory.reduce((sum, trade) => sum + (trade.profit || 0), 0).toFixed(2)}
                    </span>
                  </div>
                  <button
                    onClick={() => setActiveTab('trade')}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <TrendingUp className="w-4 h-4" />
                    <span>{tt('Вернуться к торговле')}</span>
                  </button>
                </div>
              </div>
              
              <TradeHistory trades={tradeHistory} />
            </div>
          ) : activeTab === 'calendar' ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">{tt('Экономический календарь')}</h2>
                <button
                  onClick={() => setActiveTab('trade')}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>{tt('Вернуться к торговле')}</span>
                </button>
              </div>
              <EconomicCalendar />
            </div>
          ) : activeTab === 'fundamental' ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">{tt('Фундаментальный анализ')}</h2>
                <button
                  onClick={() => setActiveTab('trade')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>{tt('Вернуться к торговле')}</span>
                </button>
              </div>
              <FundamentalAnalysis />
            </div>
          ) : activeTab === 'messages' ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">{tt('Мои сообщения')}</h2>
                <button
                  onClick={() => setActiveTab('trade')}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>{tt('Вернуться к торговле')}</span>
                </button>
              </div>
              <ClientMessages />
            </div>
          ) : (
            <div className="p-3 lg:p-6">
              
              {/* TradingView Chart - Professional Real-Time Charts */}
              <div className="mb-4">
                <TradingViewWidget 
                  asset={selectedAsset}
                  onPriceUpdate={handlePriceUpdate}
                />
              </div>
              
              {/* Trade Sections */}
              <div className="bg-gray-800 rounded-lg">
                <div className="flex border-b border-gray-700 text-sm lg:text-base">
                  <button
                    onClick={() => setTradesTab('open')}
                    className={`px-3 lg:px-6 py-2 lg:py-3 font-medium transition-colors ${
                      tradesTab === 'open' 
                        ? 'text-white border-b-2 border-green-500 bg-gray-700' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Открытые сделки
                    ({activeTrades.length})
                  </button>
                  <button
                    onClick={() => {
                      setTradesTab('history');
                      if (refreshData) {
                        refreshData();
                      }
                    }}
                    className={`px-3 lg:px-6 py-2 lg:py-3 font-medium transition-colors ${
                      tradesTab === 'history' 
                        ? 'text-white border-b-2 border-green-500 bg-gray-700' 
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    История сделок
                    ({tradeHistory.length})
                  </button>
                </div>
                
                <div className="p-4">
                  {tradesTab === 'open' ? (
                    activeTrades.length > 0 ? (
                      <div className="space-y-3">
                        {activeTrades.map(trade => (
                          <ActiveTradeCard 
                            key={trade.id} 
                            trade={trade} 
                            currentPrice={assetPrices[trade.asset]}
                            onTradeExpired={closeTrade}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <h3 className="text-lg mb-2">{tt('Нет открытых сделок')}</h3>
                        <p className="text-sm">{tt('Откройте сделку для торговли')}</p>
                      </div>
                    )
                  ) : tradesTab === 'history' ? (
                    <TradeHistory trades={tradeHistory} />
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Модальные окна */}
      {showDepositModal && (
        <DepositModal 
          onClose={() => setShowDepositModal(false)}
        />
      )}
      
      {showWithdrawModal && (
        <WithdrawModal 
          balance={balance} /* Debug: balance from useTradingEngine = ${balance} */
          onClose={() => setShowWithdrawModal(false)}
          onSuccess={() => {
            setShowWithdrawModal(false);
            // Обновить баланс если нужно
          }}
        />
      )}

      {/* Documents Modal */}
      {showDocumentsModal && (
        <DocumentsModal
          isOpen={showDocumentsModal}
          onClose={() => setShowDocumentsModal(false)}
        />
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={() => setShowChangePasswordModal(false)}
          user={user}
        />
      )}
      
      {/* Support Button */}
      <SupportButton />
    </div>
  );
};

export default TradingDemo;